import React, { useState, useRef, useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { useScrollReveal } from '@/hooks/useScrollReveal.hook';
import { programContent } from '@/data/staticProgramData';
import {
  programRegistrationInitialValues,
  programRegistrationSchema,
} from './validationSchema';
import { submitRegistration } from '@/services/RegistrationService';
import './ProgramRegistrationForm.scss';

const ConsentText = ({ text, linkLabel, linkUrl }) => {
  const parts = text.split(linkLabel);

  if (parts.length < 2) {
    return <span>{text}</span>;
  }

  return (
    <>
      {parts[0]}
      <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="program-registration-form__link">
        {linkLabel}
      </a>
      {parts.slice(1).join(linkLabel)}
    </>
  );
};

const pushDataLayer = (event) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event });
};

const ProgramRegistrationForm = ({
  variant = 'hero',
  enableScrollReveal = true,
  selectedInterest = '',
  onInterestChange = () => {},
}) => {
  const formCopy = programContent.hero.form;
  const [submitted, setSubmitted] = useState(false);
  const { wrapperRef, isVisible } = useScrollReveal({ repeat: true });
  const formStartFired = useRef(false);

  const handleFormInteraction = useCallback(() => {
    if (!formStartFired.current) {
      formStartFired.current = true;
      pushDataLayer('lead_form_start');
    }
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await submitRegistration(values);
      pushDataLayer('lead_form_submit');
      setSubmitted(true);
      resetForm({ values: { ...programRegistrationInitialValues, interest: selectedInterest } });
    } catch (error) {
      console.error("Error al registrar:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const alwaysVisible = !enableScrollReveal;

  return (
    <FlexContainer
      className={classnames('program-registration-form flex-column', {
        'program-scroll-reveal--visible': alwaysVisible || isVisible,
        'program-registration-form--section': variant === 'section',
      })}
      wrapperRef={enableScrollReveal ? wrapperRef : undefined}
    >
      <Formik
        initialValues={{ ...programRegistrationInitialValues, interest: selectedInterest }}
        enableReinitialize={true}
        validationSchema={programRegistrationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="program-registration-form__card" noValidate onFocus={handleFormInteraction}>

            <FlexContainer className="program-registration-form__row" row wrap>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="firstName"
                  type="text"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.firstName}
                  autoComplete="given-name"
                />
                <ErrorMessage name="firstName" component="span" className="program-registration-form__error" />
              </FlexContainer>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="paternalLastName"
                  type="text"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.paternalLastName}
                  autoComplete="family-name"
                />
                <ErrorMessage
                  name="paternalLastName"
                  component="span"
                  className="program-registration-form__error"
                />
              </FlexContainer>
            </FlexContainer>

            <FlexContainer className="program-registration-form__row" row wrap>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="maternalLastName"
                  type="text"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.maternalLastName}
                  autoComplete="additional-name"
                />
                <ErrorMessage
                  name="maternalLastName"
                  component="span"
                  className="program-registration-form__error"
                />
              </FlexContainer>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="dni"
                  type="text"
                  maxLength="8"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.dni}
                  autoComplete="off"
                />
                <ErrorMessage name="dni" component="span" className="program-registration-form__error" />
              </FlexContainer>
            </FlexContainer>

            <FlexContainer className="program-registration-form__row" row wrap>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="phone"
                  type="tel"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.phone}
                  autoComplete="tel"
                />
                <ErrorMessage name="phone" component="span" className="program-registration-form__error" />
              </FlexContainer>
              <FlexContainer className="program-registration-form__field flex-column">
                <Field
                  name="email"
                  type="email"
                  className="program-registration-form__input"
                  placeholder={formCopy.fields.email}
                  autoComplete="email"
                />
                <ErrorMessage name="email" component="span" className="program-registration-form__error" />
              </FlexContainer>
            </FlexContainer>

            <FlexContainer className="program-registration-form__row" row wrap>
              <FlexContainer className="program-registration-form__field program-registration-form__field--full flex-column">
                <Field
                  as="select"
                  name="interest"
                  className={`program-registration-form__select${values.interest ? ' has-value' : ''}`}
                  onChange={(e) => {
                    setFieldValue('interest', e.target.value);
                    onInterestChange(e.target.value);
                  }}
                >
                  {formCopy.interestOptions.map((opt) => (
                    <option key={opt.value || 'placeholder'} value={opt.value} disabled={!opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="interest" component="span" className="program-registration-form__error" />
              </FlexContainer>
            </FlexContainer>

            <FlexContainer className="program-registration-form__legal" row>
              <Field
                type="checkbox"
                name="dataConsent"
                className="program-registration-form__checkbox"
              />
              <p className="program-registration-form__legal-text">
                <ConsentText
                  text={formCopy.dataConsent}
                  linkLabel={formCopy.privacyLinkLabel}
                  linkUrl={formCopy.privacyUrl}
                />
              </p>
            </FlexContainer>
            <ErrorMessage name="dataConsent" component="span" className="program-registration-form__error" />

            <FlexContainer className="program-registration-form__legal" row>
              <Field
                type="checkbox"
                name="commercialConsent"
                className="program-registration-form__checkbox"
              />
              <p className="program-registration-form__legal-text">
                <ConsentText
                  text={formCopy.commercialConsent}
                  linkLabel={formCopy.privacyLinkLabel}
                  linkUrl={formCopy.privacyUrl}
                />
              </p>
            </FlexContainer>

            {submitted ? (
              <FlexContainer
                className="program-registration-form__success flex-column align-items-center"
                role="status"
                aria-live="polite"
              >
                <i
                  className="fa-solid fa-circle-check program-registration-form__success-icon"
                  aria-hidden="true"
                />
                <p className="program-registration-form__success-text">{formCopy.successMessage}</p>
              </FlexContainer>
            ) : null}

            <button
              type="submit"
              className="program-cta program-cta--primary program-registration-form__submit"
              disabled={isSubmitting}
            >
              {formCopy.submit}
            </button>
          </Form>
        )}
      </Formik>
    </FlexContainer>
  );
};

ProgramRegistrationForm.propTypes = {
  variant: PropTypes.oneOf(['hero', 'section']),
  enableScrollReveal: PropTypes.bool,
  selectedInterest: PropTypes.string,
  onInterestChange: PropTypes.func,
};

ProgramRegistrationForm.defaultProps = {
  variant: 'hero',
  enableScrollReveal: true,
  selectedInterest: '',
  onInterestChange: () => {},
};

export default ProgramRegistrationForm;
